import { Filter } from '@cronos-app/utils';
import { useRouterWithI18n } from '@ui/locales/browserSSG';
import { useCallback, useMemo } from 'react';

export const filters = {
  example: new Filter<'a' | 'b'>(
    'example',
    (queryStr) => {
      if (queryStr === 'a') return 'a';
      if (queryStr === 'b') return 'b';
    },
    (value) => value,
  ),
  emailToken: new Filter<string | undefined>(
    'emailToken',
    (queryStr) => queryStr,
    (value) => value ?? '',
  ),
  emailAddress: new Filter<string | undefined>(
    'emailAddress',
    (queryStr) => queryStr,
    (value) => value ?? '',
  ),
  missionSection: new Filter<'faq' | undefined>(
    'missionSection',
    (queryStr) => {
      if (queryStr === 'faq') return 'faq';
    },
    (value) => value ?? '',
  ),
  cdcCardView: new Filter<'second' | undefined>(
    'view',
    (queryStr) => {
      if (queryStr === 'second') return 'second';
    },
    (value) => value ?? '',
  ),
  topUpTab: new Filter<'history' | undefined>(
    'topUpTab',
    (queryStr) => {
      if (queryStr === 'history') return 'history';
    },
    (value) => value ?? '',
  ),
} as const;

export const useFilter = <T>(filter: Filter<T>, defaultValue: T) => {
  const { query, push, replace, pathname } = useRouterWithI18n();
  const raw = query[filter.key];
  const filterValue = useMemo(
    () =>
      filter.parse(typeof raw === 'string' ? raw : undefined) ?? defaultValue,
    [defaultValue, filter, raw],
  );
  const updateFilter = useCallback(
    (value: T, _replace?: boolean, scroll?: boolean) => {
      const nextStr =
        value === defaultValue ? undefined : filter.stringify(value);
      if (nextStr === raw) return;
      const nextQuery = { ...query };
      if (nextStr === undefined) {
        delete nextQuery[filter.key];
      } else {
        nextQuery[filter.key] = nextStr;
      }
      const method = _replace ? replace : push;
      method(
        {
          pathname,
          query: nextQuery,
        },
        {
          scroll,
        },
      );
    },
    [defaultValue, filter, pathname, push, query, raw, replace],
  );
  return {
    filterValue,
    updateFilter,
  };
};
